<template>
  <div class="flex flex-col divide-y divide-gray-50 rounded-8 border border-gray-100">
    <div class="p-20 min-h-100 bg-white rounded-t-8 flex items-center">
      <div class="flex flex-col gap-8">
        <div class="flex items-center gap-12">
          <div v-if="!isEditing" class="text-18 font-semibold">{{ editableGroupName }}</div>
          <SparkInput
            v-else
            ref="groupNameInput"
            v-model="editableGroupName"
            name="groupName"
            custom-class="h-40"
            class="w-auto text-18 font-semibold"
            :autofocus="true"
            @blur="saveGroupName"
            @keyup.enter="saveGroupName"
          />
          <span v-if="!isEditing" class="text-14 cursor-pointer text-gray-500" @click="editGroupName">
            <i class="fas fa-pencil-alt text-16" />
          </span>
        </div>
      </div>
    </div>
    <div class="flex divide-x divide-gray-100 bg-gray-50 rounded-b-8 text-13">
      <div class="p-20">
        <div class="text-gray-500">Created</div>
        <div class="mt-4 font-medium text-gray-800">{{ groupCreatedAt }}</div>
      </div>
      <div class="p-20">
        <div class="text-gray-500">By</div>
        <div class="mt-4 font-medium text-gray-800">{{ groupCreator }}</div>
      </div>
      <div class="p-20">
        <div class="text-gray-500">Excel file</div>
        <div
          class="mt-4 font-medium text-green-500 cursor-pointer"
          @click="requestExcelExport"
          title="Download part list"
        >
          part_list.xlsx
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapMutations } from 'vuex';

import SparkInput from '@/components/SparkComponents/SparkInput.vue';
import { formatDate } from '@/helpers';
import { saveAs } from 'file-saver';

export default {
  name: 'GroupDetailsTitle',

  components: { SparkInput },

  computed: {
    ...mapState(['batches', 'partLibraryData', 'fetchExcelListReportXLS']),
    ...mapState('application', ['axiosInstance']),

    groupName() {
      if (!this.batches.results) return '';
      const batch = this.batches.results.find(batch => batch.batch_pk === this.$route.params.id);
      return batch ? batch.batch_name : '';
    },

    groupCreatedAt() {
      if (!this.batches.results) return '';
      const batch = this.batches.results.find(batch => batch.batch_pk === this.$route.params.id);
      return batch ? formatDate(batch.created_at) : '';
    },

    groupCreator() {
      if (!this.batches.results) return '';
      const batch = this.batches.results.find(batch => batch.batch_pk === this.$route.params.id);
      return batch ? batch.created_by : '';
    },
  },

  data() {
    return {
      isEditing: false,
      editableGroupName: '',
      batchParts: {},
    };
  },

  watch: {
    groupName: {
      handler(newVal) {
        this.editableGroupName = newVal;
      },
      immediate: true,
    },
  },
  methods: {
    ...mapActions(['fetchBatches', 'fetchPartLibraryData']),
    ...mapMutations(['updateFetchExcelListReportXLS']),

    editGroupName() {
      this.isEditing = true;
      this.$nextTick(() => {
        setTimeout(() => {
          const inputElement = this.$refs.groupNameInput.$el.querySelector('input');
          if (inputElement) {
            inputElement.focus();
            inputElement.select();
          }
        }, 100);
      });
    },
    async saveGroupName() {
      this.isEditing = false;
      try {
        await this.axiosInstance.put(`api/v1/batch/${this.$route.params.id}/`, { batch_name: this.editableGroupName });
        this.fetchBatches({ page: 1, search: '' });
        this.batches.results.find(batch => batch.batch_pk === this.$route.params.id).batch_name =
          this.editableGroupName;
        this.$root.notify('success', 'Group name updated', '', 3000);
      } catch (error) {
        this.$root.notify('error', 'Error updating group name', '', 3000);
      }
    },

    async requestExcelExport() {
      await this.fetchPartLibraryData({ batches: { batch_pk: this.$route.params.id }, all_parts: true });
      this.batchParts = Object.fromEntries(this.partLibraryData.all_parts?.map(row => [row.part_id, true]) ?? []);
      await this.axiosInstance
        .post(`api/v1/excel-list-generator/`, {
          parts: this.batchParts,
        })
        .then(() => {
          this.$root.notify('success', 'File requested', `Generating Excel File.`, 3000);
        });
    },

    async fetchExcelListReportFile(newVal) {
      if (newVal) {
        await this.axiosInstance
          .get('api/v1/fetch-excel-list-generated/', {
            responseType: 'blob',
            params: {
              parts: this.batchParts,
            },
          })
          .then(response => {
            saveAs(response.data, 'part_list.xlsx');
          })
          .catch(error => {
            console.error(error);
          });
        this.updateFetchExcelListReportXLS(false);
      }
    },
  },
};
</script>
